<template>
  <b-modal id="sales-report-export-modal" v-model="visible" size="lg" centered hide-header hide-footer
           modal-class="sre-modal" body-class="sre-modal__body">
    <div class="sre">
      <!-- Hero -->
      <div class="sre__hero">
        <div class="sre__hero-icon"><i class="fa-regular fa-file-excel"></i></div>
        <div class="sre__hero-text">
          <h4 class="sre__hero-title">{{ $t('salesDashboard.exportModal.title') }}</h4>
          <span class="sre__hero-sub">{{ $t('salesDashboard.exportModal.subtitle') }}</span>
        </div>
        <button type="button" class="sre__close" @click="visible = false"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <div class="sre__inner">
        <!-- Period -->
        <div class="sre__period">
          <i class="fa-regular fa-calendar mr-50"></i>
          <span>{{ $t('salesDashboard.exportModal.period') }}: <strong>{{ periodLabel }}</strong></span>
        </div>

        <b-row>
          <b-col md="6" class="mb-1">
            <label class="sre__label">{{ $t('filters.desk') }}</label>
            <desk-select-box v-model="desks" :select-props="dropdownProps" @resolved="onDeskResolved"/>
          </b-col>
          <b-col md="6" class="mb-1">
            <label class="sre__label">{{ $t('filters.orgIds') }}</label>
            <organization-select-box v-model="form.orgIds" multiple :select-props="dropdownProps"/>
          </b-col>
          <b-col md="6" class="mb-1">
            <label class="sre__label">{{ $t('filters.businessUnit') }}</label>
            <business-unit-select-box v-model="form.buIds" multiple :selected-organization="form.orgIds"
                                      :select-props="dropdownProps"/>
          </b-col>
          <b-col md="12" class="mb-1">
            <div class="sre__users-head">
              <label class="sre__label mb-0">{{ $t('salesDashboard.exportModal.agents') }}</label>
              <div class="sre__users-actions">
                <b-button size="sm" variant="flat-primary" @click="selectAllUsers">
                  {{ $t('salesDashboard.exportModal.selectAll') }}
                </b-button>
                <b-button size="sm" variant="flat-secondary" @click="form.userIds = []">
                  {{ $t('salesDashboard.exportModal.clearAll') }}
                </b-button>
              </div>
            </div>
            <user-select-box v-model="form.userIds" multi :organizations="form.orgIds" :business-units="form.buIds"
                             :select-props="dropdownProps"/>
            <small class="text-muted" v-if="!form.userIds.length">
              <i class="fa-solid fa-circle-info mr-25"></i>{{ $t('salesDashboard.exportModal.allAgentsHint') }}
            </small>
            <small class="text-muted" v-else>{{ form.userIds.length }} {{ $t('salesDashboard.exportModal.agentsSelected') }}</small>
          </b-col>
        </b-row>

        <!-- What's inside -->
        <div class="sre__info">
          <div class="sre__info-item"><i class="fa-solid fa-table-list"></i>{{ $t('salesDashboard.exportModal.sheetSummary') }}</div>
          <div class="sre__info-item"><i class="fa-solid fa-phone-volume"></i>{{ $t('salesDashboard.exportModal.sheetSales') }}</div>
          <div class="sre__info-item"><i class="fa-solid fa-coins"></i>{{ $t('salesDashboard.exportModal.sheetRetention') }}</div>
        </div>

        <div class="sre__footer">
          <b-button variant="outline-secondary" @click="visible = false" :disabled="downloading">
            {{ $t('operations.cancel') }}
          </b-button>
          <b-button variant="success" @click="download" :disabled="downloading">
            <b-spinner v-if="downloading" small class="mr-50"/>
            <i v-else class="fa-solid fa-download mr-50"></i>
            {{ downloading ? $t('salesDashboard.exportModal.generating') : $t('salesDashboard.exportModal.export') }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import DeskSelectBox from '@/components/widget/select/DeskSelectBox.vue';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue';
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox.vue';
import UserSelectBox from '@/components/widget/select/UserSelectBox.vue';

export default {
  name: 'SalesReportExportModal',
  mixins: [mixinBase, mixinNotifications],
  components: {
    DeskSelectBox,
    OrganizationSelectBox,
    BusinessUnitSelectBox,
    UserSelectBox,
  },
  data: () => ({
    visible: false,
    downloading: false,
    desks: [],
    range: { from: null, until: null },
    form: {
      orgIds: [],
      buIds: [],
      userIds: [],
    },
  }),
  computed: {
    ...mapGetters('data', ['allUsers', 'allOrganizations']),
    // Render the dropdown menus on <body> so they float OVER the modal instead
    // of being clipped by its rounded (overflow: hidden) container.
    dropdownProps() {
      return { appendToBody: true };
    },
    periodLabel() {
      const f = this.range.from ? this.$moment(this.range.from).format('DD/MM/YYYY') : '…';
      const u = this.range.until ? this.$moment(this.range.until).format('DD/MM/YYYY') : '…';
      return `${f} → ${u}`;
    },
    // Same narrowing rules as UserSelectBox: org + BU filters combined (AND).
    visibleUsers() {
      let users = this.allUsers || [];
      if (this.form.orgIds.length) {
        users = users.filter(u => Array.isArray(u.orgIds) && u.orgIds.some(o => this.form.orgIds.includes(o)));
      }
      if (this.form.buIds.length) {
        users = users.filter(u => Array.isArray(u.businessUnits) && u.businessUnits.some(b => this.form.buIds.includes(b)));
      }
      return users;
    },
  },
  methods: {
    ...mapActions('dashboard', ['downloadSalesReport']),
    /**
     * @param {Object} ctx { range:{from,until}, desks, orgIds, buIds } — the
     * dashboard's current filters, used as the modal defaults.
     */
    open(ctx) {
      ctx = ctx || {};
      this.range = { from: ctx.range?.from || null, until: ctx.range?.until || null };
      this.form = {
        orgIds: [...(ctx.orgIds || [])],
        buIds: [...(ctx.buIds || [])],
        userIds: [],
      };
      // Inherit the dashboard's desk selection (display only — the matching
      // orgIds already arrived through ctx; assigning data directly does not
      // re-fire the resolved handler).
      this.desks = Array.isArray(ctx.desks) ? [...ctx.desks] : [];
      this.downloading = false;
      this.visible = true;
    },
    // Picking desks auto-fills the organization list (same behavior as the
    // dashboard filter card).
    onDeskResolved(resolved) {
      if (resolved && Array.isArray(resolved.orgIds)) {
        this.form.orgIds = resolved.orgIds;
      }
    },
    selectAllUsers() {
      this.form.userIds = this.visibleUsers.map(u => u.id);
    },
    download() {
      if (!this.range.from) {
        this.showWarningNotification(this.$t('salesDashboard.exportModal.noRange'));
        return;
      }
      this.downloading = true;
      this.downloadSalesReport({
        range: this.range,
        orgIds: this.form.orgIds,
        buIds: this.form.buIds,
        userIds: this.form.userIds,
      })
        .then(() => {
          this.visible = false;
        })
        .catch(err => {
          this.showServerErrorNotification('fetchDataError', 'salesDashboard', err);
        })
        .finally(() => {
          this.downloading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.sre-modal {
  .modal-content { border: none; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 60px -20px rgba(20, 24, 50, 0.45); }
  .modal-body.sre-modal__body { padding: 0; }
}
.sre {
  &__hero {
    display: flex; align-items: center; gap: 1rem; padding: 1.2rem 1.5rem; color: #fff;
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }
  &__hero-icon { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 13px; font-size: 1.3rem; background: rgba(255, 255, 255, 0.22); flex-shrink: 0; }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.2rem; color: #fff; }
  &__hero-sub { font-size: 0.82rem; opacity: 0.9; }
  &__close { border: none; background: rgba(255, 255, 255, 0.18); color: #fff; width: 34px; height: 34px; border-radius: 10px; font-size: 1rem; cursor: pointer; flex-shrink: 0; transition: all .2s ease; &:hover { background: rgba(255, 255, 255, 0.32); transform: rotate(90deg); } }

  &__inner { padding: 1.1rem 1.4rem 1.3rem; }
  &__period {
    display: inline-flex; align-items: center;
    padding: 0.45rem 0.85rem; margin-bottom: 1rem; border-radius: 10px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.02));
    border: 1px solid rgba(16, 185, 129, 0.18);
    font-size: 0.85rem;
  }
  &__label {
    display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.04em; color: #6e6b7b; margin-bottom: 0.3rem;
  }
  &__users-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.3rem; }
  &__users-actions { display: flex; gap: 0.25rem; }

  &__info {
    display: flex; flex-wrap: wrap; gap: 0.5rem 1.25rem;
    padding: 0.7rem 0.85rem; margin-top: 0.4rem; border-radius: 12px;
    background: rgba(110, 117, 159, 0.06);
  }
  &__info-item {
    display: inline-flex; align-items: center; gap: 0.45rem;
    font-size: 0.78rem; color: #6e6b7b;
    i { color: #10b981; }
  }

  &__footer { display: flex; justify-content: flex-end; gap: 0.6rem; margin-top: 1.1rem; }
}

body.dark-layout {
  .sre-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .sre__label { color: #b4b7bd; }
  .sre__info { background: rgba(255, 255, 255, 0.05); }
  .sre__info-item { color: #8b93ab; }
}

/* vue-select menus appended to <body> (appendToBody) detach from the
   BaseFilterSelect wrapper, so they lose its scoped styling and its z-index
   context. Restore the look and float them above the bootstrap modal (1050). */
body > .vs__dropdown-menu {
  z-index: 2000 !important;
  border: 1px solid rgba(34, 41, 47, 0.10);
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  padding: 4px;
  max-height: 280px;

  .vs__dropdown-option {
    border-radius: 6px;
    padding: 7px 10px;
    margin: 1px 0;
    font-size: 0.85rem;
    color: #5e5873;
    display: flex;
    align-items: center;
    white-space: normal;
  }
  .vs__dropdown-option--highlight {
    background: rgba(0, 123, 255, 0.12);
    color: #0062cc;
  }
  .vs__dropdown-option--selected {
    font-weight: 600;
    color: #0062cc;
    &::after {
      content: '\2713';
      margin-left: auto;
      padding-left: 8px;
      font-weight: 700;
    }
  }
}
body.dark-layout > .vs__dropdown-menu {
  background: #283046;
  border-color: rgba(255, 255, 255, 0.10);
  .vs__dropdown-option { color: #d0d2d6; }
}
</style>
